import type { Directive } from 'vue'
// import loadingIcon from '@/assets/images/loading/universal-loading-popup.ani.png'
import loadingIcon from '@/assets/images/loading/universal-loading-popup.webp'
import { APP_SCALE_ROOT_ID } from '@/composables/useZoomScale'
import { useLocaleStore } from '@/stores'

// ==================== 配置 ====================
const DELAY_SHOW_TIME = 300 // 视觉延迟显示时间(ms)

// ==================== 类型增强 ====================
interface LoadingHTMLElement extends HTMLElement {
  _loadingWrapper?: HTMLDivElement
  _mountTarget?: HTMLElement
  _mountToOverlay?: boolean
  _showVisualTimeout?: ReturnType<typeof setTimeout>
}

function resolveOverlayMountTarget(): HTMLElement {
  return document.getElementById(APP_SCALE_ROOT_ID) ?? document.body
}

// ==================== 核心调度 ====================
/**
 * 统一处理 Loading 的显示与隐藏。
 * - true: 立即挂载透明蒙层(阻挡点击)，延迟后显示视觉元素
 * - false: 移除视觉与蒙层，如有待执行延迟则直接取消
 */
const syncLoading = (el: LoadingHTMLElement, value: boolean) => {
  const loadingWrapper = el._loadingWrapper
  if (!loadingWrapper) return

  const mountTarget = el._mountToOverlay ? resolveOverlayMountTarget() : el
  el._mountTarget = mountTarget

  // --- 显示 ---
  if (value) {
    // 防止重复设置延迟任务（快速连续触发 true）
    if (el._showVisualTimeout) return

    // 蒙层立即挂载（透明，只阻挡交互）
    mountTarget.appendChild(loadingWrapper)

    // 延迟后显示背景与内容
    el._showVisualTimeout = setTimeout(() => {
      loadingWrapper.classList.add('bg-black/20')
      const content = loadingWrapper.firstElementChild as HTMLElement
      // if (content) content.style.visibility = 'visible'
      if (content) content.style.opacity = '1'
      el._showVisualTimeout = undefined
    }, DELAY_SHOW_TIME)
    return
  }

  // --- 隐藏 ---
  // 情况 A：延迟尚未触发，直接取消并移除蒙层
  if (el._showVisualTimeout) {
    clearTimeout(el._showVisualTimeout)
    el._showVisualTimeout = undefined
    loadingWrapper.remove()
    return
  }

  // 情况 B：视觉已完全显示，重置样式后移除蒙层
  loadingWrapper.classList.remove('bg-black/20')
  const content = loadingWrapper.firstElementChild as HTMLElement
  // if (content) content.style.visibility = 'hidden'
  if (content) content.style.opacity = '0'
  loadingWrapper.remove()
}

// ==================== 指令定义 ====================
const vLoading: Directive<LoadingHTMLElement, boolean> = {
  /**
   * 初始化：创建 Loading DOM 并挂载到合适的目标。
   * @param el 绑定指令的元素
   * @param binding 指令绑定对象，支持修饰符:
   *   - .self: 蒙层相对于当前元素定位(absolute)，否则挂到缩放层(fixed)，没有缩放层时再退回 body
   */
  mounted(el, binding) {
    const { t } = useLocaleStore()

    // 1. 确定挂载目标：全屏蒙层优先挂到缩放层，没有缩放层时再退回 body
    const isMountToBody = !binding.modifiers.self
    el._mountToOverlay = isMountToBody

    // 2. 创建透明蒙层（挂载后即刻阻挡点击，视觉延迟通过 bg/visibility 控制）
    const loadingWrapper = document.createElement('div')
    loadingWrapper.className = [
      isMountToBody ? 'fixed inset-0' : 'absolute inset-0',
      'flex items-center justify-center', // 初始无背景色
      isMountToBody ? 'z-[9999]' : 'z-50',
      'transition-colors duration-300',
    ].join(' ')

    // 3. 创建内容容器（初始隐藏）
    const loadingContent = document.createElement('div')
    loadingContent.className = [
      'glass-frosted rounded-6xl',
      'px-6 py-4',
      'flex flex-col items-center justify-center',
      'gap-3',
      'transition-opacity duration-500',
    ].join(' ')
    // loadingContent.style.visibility = 'hidden'
    loadingContent.style.opacity = '0'

    // 4. 图标
    const iconEl = document.createElement('img')
    iconEl.src = loadingIcon
    iconEl.className = 'w-40 h-40 object-contain'
    iconEl.draggable = false
    iconEl.style.userSelect = 'none'

    // 5. 文字
    const textEl = document.createElement('p')
    textEl.textContent = t('common.loading')
    textEl.className = 'text-black-white text-m-medium'

    // 6. DOM 组合
    loadingContent.appendChild(iconEl)
    loadingContent.appendChild(textEl)
    loadingWrapper.appendChild(loadingContent)

    // 7. 局部模式：确保父元素定位上下文
    if (!isMountToBody) {
      const pos = getComputedStyle(el).position
      if (!['relative', 'absolute', 'fixed'].includes(pos)) {
        el.classList.add('relative')
      }
    }

    el._loadingWrapper = loadingWrapper

    // 8. 根据当前绑定值应用初始状态
    syncLoading(el, binding.value)
  },

  /**
   * 值变化时更新显示/隐藏。
   */
  updated(el, binding) {
    if (binding.value === binding.oldValue) return
    syncLoading(el, binding.value)
  },

  /**
   * 卸载时清理所有资源。
   */
  unmounted(el) {
    // 取消待执行的延迟
    if (el._showVisualTimeout) {
      clearTimeout(el._showVisualTimeout)
      el._showVisualTimeout = undefined
    }
    // 从 DOM 中移除蒙层
    el._loadingWrapper?.remove()
    // 清理引用
    el._loadingWrapper = undefined
    el._mountTarget = undefined
    el._mountToOverlay = undefined
  },
}

export default vLoading
